import { AttributeTypes, DataTypes } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Button, FormField, Input, Modal, RadioGroup, RadioGroupProps, SpaceBetween } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';

import { constants } from '../../../common/Localization';
import { toCamelCase } from '../utils';
interface AddAttributeModalProp {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    existingAttributes: string[];
    onConfirmAddAttribute: (attrName: string, attrType: string, dataType: string) => Promise<void>;
}

const uomHintMsgs: {
    [key in Exclude<AttributeTypes, AttributeTypes.GENERAL>]: string;
} = {
    [AttributeTypes.STORAGE]: constants.ADD_ATTRIBUTE_UOM_HINT_STORAGE,
    [AttributeTypes.FREQUENCY]: constants.ADD_ATTRIBUTE_UOM_HINT_FREQUENCY,
    [AttributeTypes.RATIO]: constants.ADD_ATTRIBUTE_UOM_HINT_RATIO,
};

const attributeTypeOptions: RadioGroupProps.RadioButtonDefinition[] = [
    { value: AttributeTypes.GENERAL, label: constants.ATTRIBUTE_TYPE_RADIO_BUTTON_GENERAL },
    { value: AttributeTypes.STORAGE, label: constants.ATTRIBUTE_TYPE_RADIO_BUTTON_STORAGE },
    { value: AttributeTypes.FREQUENCY, label: constants.ATTRIBUTE_TYPE_RADIO_BUTTON_FREQUENCY },
    { value: AttributeTypes.RATIO, label: constants.ATTRIBUTE_TYPE_RADIO_BUTTON_RATIO },
];


const AddAttributeModal = (props: AddAttributeModalProp): JSX.Element => {
    const [attributeName, setAttributeName] = useState('');
    const [attributeType, setAttributeType] = useState<AttributeTypes>(AttributeTypes.GENERAL);
    const [dataType, setDataType] = useState<DataTypes>(DataTypes.TEXT);
    const [errorMsg, setErrorMsg] = useState('');
    const [uomHint, setUomHint] = useState('');

    const dataTypeOptions: RadioGroupProps.RadioButtonDefinition[]= [
        { value: DataTypes.TEXT, label: constants.DATA_TYPE_RATIO_BUTTON_TEXT, disabled: attributeType !== AttributeTypes.GENERAL },
        { value: DataTypes.NUMBER, label: constants.DATA_TYPE_RATIO_BUTTON_NUMBER, disabled: false },
        { value: DataTypes.BOOLEAN, label: constants.DATA_TYPE_RATIO_BUTTON_BOOLEAN, disabled: attributeType !== AttributeTypes.GENERAL },
    ];

    useEffect(() => {
        const camelName = toCamelCase(attributeName);
        if (props.existingAttributes.includes(camelName)) {
            setErrorMsg(constants.ADD_ATTRIBUTE_DUPLICATE_ERROR);
        } else {
            setErrorMsg('');
        }
    }, [attributeName, props.existingAttributes]);

    useEffect(() => {
        if (attributeType !== AttributeTypes.GENERAL) {
            setDataType(DataTypes.NUMBER);
            setUomHint(uomHintMsgs[attributeType]);
        } else {
            setUomHint('');
        }
    }, [attributeType]);

    const isInvalidAttribute = (): boolean => {
        return !attributeName || errorMsg.length > 0;
    };
    const clearAddAttributeForms = (): void => {
        setAttributeName('');
        setAttributeType(AttributeTypes.GENERAL);
        setDataType(DataTypes.TEXT);
        setErrorMsg('');
        setUomHint('');
    };
    const dismissModal = (): void => props.setVisible(false);
    const onConfirm = (): void => {
        props.onConfirmAddAttribute(attributeName, attributeType, dataType);
        dismissModal();
        clearAddAttributeForms();
    };

    return (
        <Modal
            onDismiss={dismissModal}
            visible={props.visible}
            size="medium"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={dismissModal}>{constants.CANCEL_BUTTON_TEXT}</Button>
                        <Button variant="primary" onClick={onConfirm} disabled={isInvalidAttribute()}>
                            {constants.ADD_ATTRIBUTE_CONFIRM_BUTTON_TEXT}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
            header={constants.ADD_ATTRIBUTE_MODAL_HEADER}
        >
            <SpaceBetween size="m" direction="vertical">
                <FormField label={constants.ATTRIBUTE_NAME_FORM_LABEL} description={constants.ATTRIBUTE_NAME_FORM_DESCRIPTION} errorText={errorMsg}>
                    <Input value={attributeName} onChange={({ detail }) => setAttributeName(detail.value)} />
                </FormField>
                <FormField label={constants.ATTRIBUTE_TYPE_FORM_LABEL}>
                    <RadioGroup
                        items={attributeTypeOptions}
                        value={attributeType}
                        onChange={({ detail }) => setAttributeType(detail.value as AttributeTypes)}
                    />
                </FormField>
                <FormField label={constants.DATA_TYPE_FORM_LABEL} description={constants.DATA_TYPE_FORM_DESCRIPTION}>
                    <RadioGroup
                        items={dataTypeOptions}
                        value={dataType}
                        onChange={({ detail }) => setDataType(detail.value as DataTypes)}
                    />
                </FormField>
                <div>{uomHint}</div>
            </SpaceBetween>
        </Modal>
    );   
};

export default AddAttributeModal;
